import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { PageLayout } from "../components/page-layout";
import ScreenerExplanation from "../components/screener-explanation";

/* ---------------- API ---------------- */
async function fetchFilteredStocks(payload) {
  const res = await fetch(
    "https://b4usjefnn7.execute-api.us-east-1.amazonaws.com/Prod/classify",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  if (!res.ok) {
    throw new Error("Failed to fetch stocks");
  }

  return res.json();
}

/* ---------------- PAGE ---------------- */
export default function FiltersPage({ userEmail }) {
  const { user } = useAuth0();

  const [filters, setFilters] = useState({
    market: "au",
    lookback_years: "10",
    profit_years: "8",
    profit_ratio: "0.05",
    min_net_income: "50000000",
    max_net_income: "Inf",
    min_debt_ebitda: "-Inf",
    max_debt_ebitda: "1",
  });

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savedTickers, setSavedTickers] = useState([]);
  const [message, setMessage] = useState("");

  const storageKey = `portfolio_${user?.email || userEmail || "guest"}`;

  /* ---------------- HANDLERS ---------------- */
  const handleChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const profitYearsError =
    Number(filters.profit_years) > Number(filters.lookback_years);

  const handleSubmit = async () => {
    if (profitYearsError) return;

    const payload = {
      market: filters.market,
      last_years: Number(filters.lookback_years),
      profit_years: Number(filters.profit_years),
      profit_ratio: Number(filters.profit_ratio),
      min_net_income: filters.min_net_income,
      max_net_income: filters.max_net_income,
      min_debt_ebitda: filters.min_debt_ebitda,
      max_debt_ebitda: filters.max_debt_ebitda,
    };

    try {
      setLoading(true);
      setError(null);

      const data = await fetchFilteredStocks(payload);
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);

    if (saved) {
      try {
        setSavedTickers(JSON.parse(saved));
      } catch {
        setSavedTickers([]);
      }
    }
  }, [storageKey]);

  const handleToggleTicker = (ticker) => {
    const updated = savedTickers.includes(ticker)
      ? savedTickers.filter((t) => t !== ticker)
      : [...savedTickers, ticker];

    setSavedTickers(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));

    setMessage(
      savedTickers.includes(ticker)
        ? "Removed from Portfolio"
        : "Added to Portfolio"
    );

    setTimeout(() => setMessage(""), 2000);
  };

  const numberFieldSx = {
    width: { xs: "100%", sm: 170 },
    maxWidth: "100%",
  };

  /* ---------------- RENDER ---------------- */
  return (
    <PageLayout>
      <Container
        maxWidth={false}
        sx={{
          mt: 4,
          mb: 6,
          px: { xs: 2, sm: 3, md: 4, lg: 5 },
          width: "100%",
          maxWidth: "1800px",
        }}
      >
        <ScreenerExplanation />

        {/* ---------------- FILTER PAPER ---------------- */}
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, mt: 3 }}>
          <Typography variant="h5" gutterBottom>
            Stock Filter Criteria
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mt: 2,
              alignItems: "flex-end",
            }}
          >
            {/* Market */}
            <FormControl sx={{ minWidth: 180 }}>
              <FormLabel>Market</FormLabel>
              <RadioGroup
                row
                name="market"
                value={filters.market}
                onChange={handleChange}
              >
                <FormControlLabel value="au" control={<Radio />} label="AU" />
                <FormControlLabel value="us" control={<Radio />} label="US" />
              </RadioGroup>
            </FormControl>

            {/* Lookback + Profit Years + Profit Ratio */}
            <TextField
              label="Lookback Years"
              name="lookback_years"
              value={filters.lookback_years}
              onChange={handleChange}
              size="small"
              sx={numberFieldSx}
              inputProps={{ inputMode: "numeric" }}
            />

            <TextField
              label="Profit Years"
              name="profit_years"
              value={filters.profit_years}
              onChange={handleChange}
              size="small"
              sx={numberFieldSx}
              error={profitYearsError}
              helperText={profitYearsError ? "Cannot exceed lookback" : ""}
              inputProps={{ inputMode: "numeric" }}
            />

            <TextField
              label="Profit Ratio"
              name="profit_ratio"
              value={filters.profit_ratio}
              onChange={handleChange}
              size="small"
              sx={numberFieldSx}
            />

            {/* Net Income */}
            <TextField
              label="Min Net Income"
              name="min_net_income"
              value={filters.min_net_income}
              onChange={handleChange}
              size="small"
              sx={numberFieldSx}
            />

            <TextField
              label="Max Net Income"
              name="max_net_income"
              value={filters.max_net_income}
              onChange={handleChange}
              size="small"
              sx={numberFieldSx}
            />

            {/* Debt / EBITDA */}
            <TextField
              label="Min Debt/EBITDA"
              name="min_debt_ebitda"
              value={filters.min_debt_ebitda}
              onChange={handleChange}
              size="small"
              sx={numberFieldSx}
            />

            <TextField
              label="Max Debt/EBITDA"
              name="max_debt_ebitda"
              value={filters.max_debt_ebitda}
              onChange={handleChange}
              size="small"
              sx={numberFieldSx}
            />

            {/* Submit */}
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={profitYearsError || loading}
              sx={{ minHeight: 40 }}
            >
              {loading ? "Filtering..." : "Apply Filters"}
            </Button>
          </Box>
        </Paper>

        {/* ---------------- STATUS MESSAGES ---------------- */}
        {error && (
          <Typography color="error" sx={{ mt: 3 }}>
            {error}
          </Typography>
        )}

        {message && (
          <Typography color="primary" sx={{ mt: 2 }}>
            {message}
          </Typography>
        )}

        {/* ---------------- RESULTS ---------------- */}
        {results.length > 0 && (
          <Box sx={{ mt: 4, width: "100%", minWidth: 0 }}>
            <Typography variant="h6" gutterBottom>
              Results ({results.length})
            </Typography>

            <Paper elevation={2} sx={{ width: "100%", overflow: "hidden" }}>
              {/* Horizontal scrolling on smaller screens */}
              <Box sx={{ overflowX: "auto", width: "100%" }}>
                {/* Column headings */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "56px minmax(150px, 1.1fr) minmax(190px, 1.2fr) minmax(150px, 1fr) minmax(260px, 2fr)",
                    alignItems: "center",
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    minWidth: 850,
                    borderBottom: "1px solid #555",
                    backgroundColor: "rgba(255,255,255,0.04)",
                  }}
                >
                  <Typography variant="subtitle2">Portfolio</Typography>
                  <Typography variant="subtitle2">Ticker</Typography>
                  <Typography variant="subtitle2">Net Income</Typography>
                  <Typography variant="subtitle2">Profitable Years</Typography>
                  <Typography variant="subtitle2">Sector</Typography>
                </Box>

                {results.map((row) => (
                  <Box
                    key={row.ticker}
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "56px minmax(150px, 1.1fr) minmax(190px, 1.2fr) minmax(150px, 1fr) minmax(260px, 2fr)",
                      alignItems: "center",
                      gap: 2,
                      px: 2,
                      py: 0.5,
                      minWidth: 850,
                      borderBottom: "1px solid #333",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.035)",
                      },
                    }}
                  >
                    <Checkbox
                      checked={savedTickers.includes(row.ticker)}
                      onChange={() => handleToggleTicker(row.ticker)}
                      inputProps={{
                        "aria-label": `Add ${row.ticker} to portfolio`,
                      }}
                      sx={{ p: 1 }}
                    />

                    <Typography
                      component={RouterLink}
                      to={`/stock/${row.ticker}`}
                      fontWeight={600}
                      sx={{
                        color: "inherit",
                        textDecoration: "none",
                        overflowWrap: "anywhere",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {row.ticker}
                    </Typography>

                    <Typography sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {Number(row.netIncome_latest).toLocaleString()}
                    </Typography>

                    <Typography>
                      {row.positive_years}
                    </Typography>

                    <Typography
                      title={row.sector || "Unknown"}
                      sx={{
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        color: row.sector ? "inherit" : "text.secondary",
                      }}
                    >
                      {row.sector || "Unknown"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        )}
      </Container>
    </PageLayout>
  );
}
